import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import LegacyButton from '../LegacyButton';
import Typography from '../Typography';
import InputGroup from '../InputGroup';
import { Icons } from '@ohif/ui-next';

const StudyListFilter = ({
  filtersMeta,
  filterValues,
  onChange,
  clearFilters,
  isFiltering,
  numOfStudies,
  onUploadClick,
  getDataSourceConfigurationComponent,
}) => {
  const { t } = useTranslation('StudyList');

  const { sortBy, sortDirection } = filterValues;

  const filterSorting = {
    sortBy,
    sortDirection,
  };

  const setFilterSorting = sortingValues => {
    onChange({
      ...filterValues,
      ...sortingValues,
    });
  };

  const isSortingEnabled = numOfStudies > 0 && numOfStudies <= 100;

  return (
    <React.Fragment>
      {/* Study List Header */}
      <div className="w-full bg-gray-50">
        <div className="w-full px-6 pt-5">
          <div className="mb-5 flex w-full flex-row items-center justify-between">
            {/* Left */}
            <div className="flex min-w-[1px] shrink flex-row items-center gap-5">
              <Typography
                variant="h6"
                className="text-xl font-semibold text-gray-900"
              >
                {t('StudyList')}
              </Typography>

              {getDataSourceConfigurationComponent && getDataSourceConfigurationComponent()}

              {onUploadClick && (
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md border border-[#d52b1e] bg-white px-4 py-2 text-sm font-semibold text-[#d52b1e] transition duration-200 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-[#d52b1e]/20"
                  onClick={onUploadClick}
                >
                  <Icons.Upload />
                  <span>{t('Upload')}</span>
                </button>
              )}
            </div>

            {/* Right */}
            <div className="flex h-[40px] flex-row items-center gap-3">
              {isFiltering && (
                <LegacyButton
                  rounded="full"
                  variant="outlined"
                  color="primaryActive"
                  border="primaryActive"
                  className="mx-0"
                  startIcon={<Icons.Cancel />}
                  onClick={clearFilters}
                >
                  {t('ClearFilters')}
                </LegacyButton>
              )}

              <div className="flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm">
                <Typography
                  variant="h6"
                  className="mr-2 text-base font-semibold text-gray-900"
                  data-cy="num-studies"
                >
                  {numOfStudies > 100 ? '>100' : numOfStudies}
                </Typography>

                <Typography
                  variant="h6"
                  className="text-sm font-medium text-gray-500"
                >
                  {t('Studies')}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="sticky -top-1 z-10 w-full border-b border-gray-200 bg-white shadow-sm">
        <div className="w-full px-6 py-4">
          <InputGroup
            inputMeta={filtersMeta}
            values={filterValues}
            onValuesChange={onChange}
            sorting={filterSorting}
            onSortingChange={setFilterSorting}
            isSortingEnabled={isSortingEnabled}
          />
        </div>

        {/* Sorting Warning */}
        {numOfStudies > 100 && (
          <div className="w-full px-6 pb-3">
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-center">
              <p className="text-sm font-medium text-red-700">
                {t('Filter list to 100 studies or less to enable sorting')}
              </p>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

StudyListFilter.propTypes = {
  filtersMeta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      inputType: PropTypes.oneOf(['Text', 'MultiSelect', 'DateRange', 'None']).isRequired,
      isSortable: PropTypes.bool.isRequired,
      gridCol: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).isRequired,
      option: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        })
      ),
    })
  ).isRequired,

  filterValues: PropTypes.object.isRequired,
  numOfStudies: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  isFiltering: PropTypes.bool.isRequired,
  onUploadClick: PropTypes.func,
  getDataSourceConfigurationComponent: PropTypes.func,
};

export default StudyListFilter;
